/**
 * A wall element's plan rings. Its footprint is the outer face; a footprint
 * wide and long enough to hold a void is four walls of WALL_THICKNESS around
 * one, and the inner ring is that void's boundary — the footprint inset by
 * one thickness, corners (chamfers) followed. Narrower or shorter than two
 * thicknesses, it is one solid wall: no inner ring. Both views and the
 * exports draw the element from these, so it reads the same everywhere.
 */

import { type FloorPlanFreeElement, freeElementRing, insetConvexRing, type Point, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';

export interface WallElementRings {
  /** The footprint: the walls' outer face. */
  outer: Point[];
  /** The void's boundary (the walls' inner face); null for a solid wall. */
  inner: Point[] | null;
}

/** Below this much over two thicknesses the void would be a sliver: still one solid wall. */
const VOID_MIN = 1e-6;

export function wallElementRings(element: FloorPlanFreeElement): WallElementRings {
  const outer = freeElementRing(element);
  const hollow = element.width > 2 * WALL_THICKNESS + VOID_MIN && element.length > 2 * WALL_THICKNESS + VOID_MIN;
  const inner = hollow ? insetConvexRing(outer, WALL_THICKNESS) : [];
  return { outer, inner: inner.length ? inner : null };
}
